'use client';

import { useState, useEffect, useRef } from 'react';

interface VideoItem {
  src: string;
  label: string;
}

// ─── Video Carousel (player com controles, estilo altatech.com.br) ───────────
export default function VideoCarousel({ videos }: { videos: VideoItem[] }) {
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = videos[index];

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.play().catch(() => {});
  }, [index]);

  function toggleMute() {
    setMuted((v) => !v);
  }

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  }

  function goPrev() {
    setIndex((i) => (i - 1 + videos.length) % videos.length);
  }

  function goNext() {
    setIndex((i) => (i + 1) % videos.length);
  }

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="relative overflow-hidden bg-black w-full h-full"
        style={{ borderRadius: '4px', aspectRatio: '4/3' }}
      >
        <video
          ref={videoRef}
          key={current.src}
          src={current.src}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted={muted}
          playsInline
        />

        {/* Overlay escuro sutil para os controles terem contraste */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.3))' }}
        />

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          aria-label="Tela cheia"
          className="absolute top-3 right-3 flex items-center justify-center transition-colors duration-150"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            background: 'rgba(0,0,0,0.4)',
            color: '#fff',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>

        {/* Mudo */}
        <button
          onClick={toggleMute}
          aria-label={muted ? 'Ativar som' : 'Silenciar'}
          className="absolute bottom-3 right-3 flex items-center justify-center transition-colors duration-150"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            background: 'rgba(0,0,0,0.4)',
            color: '#fff',
          }}
        >
          {muted ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5 6 9H2v6h4l5 4z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5 6 9H2v6h4l5 4z" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7" />
            </svg>
          )}
        </button>

        {/* Setas de navegação */}
        {videos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              aria-label="Vídeo anterior"
              className="absolute flex items-center justify-center"
              style={{
                top: '50%',
                left: '-16px',
                transform: 'translateY(-50%)',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              onClick={goNext}
              aria-label="Próximo vídeo"
              className="absolute flex items-center justify-center"
              style={{
                top: '50%',
                right: '-16px',
                transform: 'translateY(-50%)',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Indicadores de slide */}
      {videos.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {videos.map((v, i) => (
            <button
              key={v.src}
              onClick={() => setIndex(i)}
              aria-label={`Ver vídeo: ${v.label}`}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: i === index ? '#1a9fff' : '#d1d5db',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
